import React, { useState, useEffect } from 'react';
import { Search, Filter } from 'lucide-react';
import { supabase, safeSupabaseOperation } from '../../lib/supabase';
import StationCard from './StationCard';
import type { Location } from '../../types/database';

const StationGrid = () => {
  const [stations, setStations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'available' | 'maintenance'>('all');

  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    try {
      setLoading(true);
      
      // Default stations for offline mode
      const defaultStations: Location[] = [
        {
          id: '1',
          name: 'Station Proxy Power Centre',
          type: 'station',
          address: 'Boulevard du Centenaire, Dakar, Sénégal'
        },
        {
          id: '2',
          name: 'Station Proxy Power Almadies',
          type: 'station',
          address: 'Route des Almadies, Dakar, Sénégal'
        },
        {
          id: '3',
          name: 'Station Proxy Power Plateau',
          type: 'station',
          address: 'Avenue Léopold Sédar Senghor, Dakar, Sénégal'
        }
      ];
      
      // Use safe operation to handle network errors gracefully
      const { data, error, isOffline } = await safeSupabaseOperation(async () => {
        if (!supabase) throw new Error('Supabase not available');
        
        const { data, error } = await supabase
          .from('locations')
          .select('*')
          .eq('type', 'station')
          .order('name');

        if (error) throw error;
        return data;
      }, defaultStations);
      
      if (isOffline) {
        console.warn('Using default stations due to network issues');
        setStations(defaultStations);
      } else if (error) {
        console.error('Error fetching stations:', error);
        setStations(defaultStations);
      } else {
        setStations(data || defaultStations);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
      // Use default stations as fallback
      setStations([
        {
          id: '1',
          name: 'Station Proxy Power Centre',
          type: 'station',
          address: 'Boulevard du Centenaire, Dakar, Sénégal'
        },
        {
          id: '2',
          name: 'Station Proxy Power Almadies',
          type: 'station',
          address: 'Route des Almadies, Dakar, Sénégal'
        },
        {
          id: '3',
          name: 'Station Proxy Power Plateau',
          type: 'station',
          address: 'Avenue Léopold Sédar Senghor, Dakar, Sénégal'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredStations = stations.filter(station => {
    const matchesSearch = station.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         station.address.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    // Dans un cas réel, nous utiliserions un statut de la base de données
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
            <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="h-8 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="flex-1 h-10 bg-gray-200 rounded"></div>
              <div className="w-20 h-10 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sticky top-0 bg-white p-4 shadow-md rounded-lg z-10">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une station..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md transition-colors ${
                filter === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Toutes
            </button>
            <button
              onClick={() => setFilter('available')}
              className={`px-4 py-2 rounded-md transition-colors ${
                filter === 'available'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Disponibles
            </button>
          </div>
        </div>
      </div>

      {filteredStations.length === 0 ? (
        <div className="text-center py-8">
          <Filter size={40} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 text-lg">Aucune station trouvée</p>
          <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredStations.map((station) => (
            <StationCard key={station.id} {...station} />
          ))}
        </div>
      )}
    </div>
  );
};

export default StationGrid;